import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { authFetch } from "../utils/authFetch";
import API_ENDPOINTS from "../apiConfig";
import { useSite } from "../context/SiteContext";
import { fetchPlatesForArea } from "../hooks/plateService";
import { contrastColor } from "../utils/colorUtils";

// ─── Trend badge ─────────────────────────────────────────────────────────────

const Trend = ({ value }) => {
    if (value === null || value === undefined) return null;
    const up = value >= 0;
    return (
        <span style={{ color: up ? "#10b981" : "#ef4444", fontSize: "0.75rem", fontWeight: 700 }}>
            {up ? "↑" : "↓"} {Math.abs(value)}%
        </span>
    );
};

// ─── KPI tile (col-6, own border) ────────────────────────────────────────────

const KpiTile = ({ value, label, sub, trendWeek, trendMonth, t }) => (
    <div className="col-6">
        <div className="border ez-rounded-tile p-3 ez-shadow h-100 bg-white">
            <div style={{ fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.1, color: "var(--ez-text)" }}>
                {value}
            </div>
            <div className="text-muted" style={{ fontSize: "0.85rem", marginTop: 2 }}>{label}</div>
            {sub && <div style={{ fontSize: "0.78rem", color: "#9ca3af", marginTop: 1 }}>{sub}</div>}
            {(trendWeek !== undefined || trendMonth !== undefined) && (
                <div className="d-flex gap-2 flex-wrap mt-1">
                    {trendWeek !== undefined && (
                        <span><Trend value={trendWeek} /> <span style={{ color: "#9ca3af", fontSize: "0.72rem" }}>7{t("day_abbr")}</span></span>
                    )}
                    {trendMonth !== undefined && (
                        <span><Trend value={trendMonth} /> <span style={{ color: "#9ca3af", fontSize: "0.72rem" }}>30{t("day_abbr")}</span></span>
                    )}
                </div>
            )}
        </div>
    </div>
);

// ─── Quick-add modal ─────────────────────────────────────────────────────────

function QuickAddModal({ plateNo, areas, selectedAreaLocal, onClose, onAdded, setPlatesByArea }) {
    const { t } = useTranslation();
    const today = new Date().toISOString().split("T")[0];

    const defaultChecked = selectedAreaLocal !== "all"
        ? { [selectedAreaLocal]: true }
        : Object.fromEntries(areas.map(a => [a.id, true]));

    const [checkedAreas, setCheckedAreas] = useState(defaultChecked);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    const toggleArea = (id) => setCheckedAreas(prev => ({ ...prev, [id]: !prev[id] }));

    const handleSubmit = async () => {
        const selectedIds = Object.keys(checkedAreas).filter(id => checkedAreas[id]);
        if (!selectedIds.length) return;
        setSaving(true);
        setError("");
        try {
            await Promise.all(selectedIds.map(areaId =>
                authFetch(`${API_ENDPOINTS.getareas}/${areaId}/license-plates`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ plate: plateNo, valid_from: today, valid_until: "", note, categories: [] }),
                }).then(r => { if (!r.ok && r.status !== 409) throw new Error(); })
            ));
            setSuccess(true);
            await Promise.all(selectedIds.map(async areaId => {
                try {
                    const plates = await fetchPlatesForArea(areaId);
                    setPlatesByArea(prev => ({ ...prev, [areaId]: plates }));
                } catch {}
            }));
            setTimeout(() => { onAdded(); onClose(); }, 900);
        } catch {
            setError(t("Error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center" }}
            onClick={e => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white rounded p-4 shadow" style={{ minWidth: 300, maxWidth: 420, width: "90%" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">{t("insights_quick_add_title")}</h6>
                    <button className="btn btn-sm btn-light" onClick={onClose}><i className="bi bi-x" /></button>
                </div>
                <div className="mb-3">
                    <div className="fw-bold" style={{ fontSize: "1.2rem", letterSpacing: 1 }}>{plateNo}</div>
                </div>
                <div className="mb-3">
                    <label className="form-label small text-muted">{t("insights_quick_add_areas")}</label>
                    <div className="d-flex flex-column gap-1">
                        {areas.map(a => (
                            <label key={a.id} className="d-flex align-items-center gap-2" style={{ cursor: "pointer" }}>
                                <input type="checkbox" className="form-check-input mt-0" checked={!!checkedAreas[a.id]} onChange={() => toggleArea(a.id)} />
                                <span>{a.name}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <div className="mb-3">
                    <input type="text" className="form-control form-control-sm" placeholder={t("insights_quick_add_note")} value={note} onChange={e => setNote(e.target.value)} />
                </div>
                {error && <div className="text-danger small mb-2">{error}</div>}
                {success && <div className="text-success small mb-2">{t("insights_quick_add_success")}</div>}
                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-sm btn-light" onClick={onClose}>{t("Cancel")}</button>
                    <button className="btn btn-sm ez-button-full" onClick={handleSubmit} disabled={saving || success || Object.values(checkedAreas).every(v => !v)}>
                        {saving ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-plus-lg me-1" />}
                        {t("add")}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

const norm = p => p?.replace(/[^A-Z0-9]/gi, "").toUpperCase();

export default function InsightsPanel({ selectedAreaLocal }) {
    const { siteId, areas: loadedAreas, platesByArea, setPlatesByArea } = useSite();
    const { t } = useTranslation();

    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [topPeriod, setTopPeriod] = useState("week");
    const [addPlate, setAddPlate] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!siteId) return;
        setLoading(true);
        const url = new URL(API_ENDPOINTS.getStatsInsights.replace("##siteid##", siteId));
        if (selectedAreaLocal !== "all") url.searchParams.set("areaId", selectedAreaLocal);
        authFetch(url.toString())
            .then(r => r.json())
            .then(data => { setInsights(data); setLoading(false); })
            .catch(() => setLoading(false));
    }, [siteId, selectedAreaLocal, refreshKey]);

    if (loading) return (
        <div className="border rounded p-3 mt-3 text-muted" style={{ fontSize: "0.85rem" }}>{t("Loading...")}</div>
    );
    if (!insights) return null;

    const { week, month, peak_hour, peak_hour_avg, area_breakdown } = insights;
    const topAllowed = insights[`top_allowed_${topPeriod}`] ?? [];
    const topDenied  = insights[`top_denied_${topPeriod}`]  ?? [];

    const fmtHour = h => `${String(h).padStart(2, "0")}:00–${String(h + 1).padStart(2, "0")}:00`;
    const allKnownPlates = Object.values(platesByArea).flat();
    const enrich = plate_no => allKnownPlates.find(p => norm(p.plate) === norm(plate_no));

    const periodToggle = (
        <div className="d-flex gap-1">
            <button className={`btn btn-sm ${topPeriod === "week"  ? "ez-button-full" : "ez-button-light"}`} onClick={() => setTopPeriod("week")}>{t("insights_week")}</button>
            <button className={`btn btn-sm ${topPeriod === "month" ? "ez-button-full" : "ez-button-light"}`} onClick={() => setTopPeriod("month")}>{t("insights_month")}</button>
        </div>
    );

    return (
        <>
            <div className="row g-2 mt-3">

                {/* ── Left: 4 KPI tiles in 2×2 + area breakdown ── */}
                <div className="col-12 col-md-5">
                    <div className="row g-2">
                        <KpiTile t={t} value={week.total.toLocaleString()} label={t("insights_passings")} sub={t("insights_week")} trendWeek={week.trend_pct} trendMonth={month.trend_pct} />
                        <KpiTile t={t} value={`${week.auth_rate}%`} label={t("Authorized")} sub={t("insights_week")} trendWeek={week.auth_rate_trend_pct} trendMonth={month.auth_rate_trend_pct} />
                        <KpiTile t={t} value={month.unique.toLocaleString()} label={t("insights_unique")} sub={t("insights_month")} />
                        <KpiTile t={t} value={fmtHour(peak_hour)} label={t("insights_peak_hour")} sub={`⌀ ${peak_hour_avg} ${t("insights_peak_avg")}`} />
                    </div>

                    {/* Area breakdown — same column, below KPIs */}
                    {selectedAreaLocal === "all" && area_breakdown?.length > 1 && (
                        <div className="border ez-rounded-tile p-3 ez-shadow mt-2 bg-white">
                            <div className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
                                <span style={{ textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.75rem" }}>{t("insights_area_breakdown")}</span>
                                <span className="fw-normal ms-1" style={{ fontSize: "0.75rem" }}>· 30{t("day_abbr")}</span>
                            </div>
                            <table className="w-100" style={{ fontSize: "0.85rem", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr className="text-muted" style={{ borderBottom: "1px solid var(--bs-border-color)" }}>
                                        <th className="pb-1 fw-normal">{t("Area name")}</th>
                                        <th className="pb-1 fw-normal text-end" title={t("insights_passings")}><i className="bi bi-arrow-left-right" /></th>
                                        <th className="pb-1 fw-normal text-end" title={t("Authorized")} style={{ color: "#10b981" }}><i className="bi bi-check-lg" /></th>
                                        <th className="pb-1 fw-normal text-end" title={t("Unauthorized")} style={{ color: "#ef4444" }}><i className="bi bi-x-lg" /></th>
                                        <th className="pb-1 fw-normal text-end">%</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {area_breakdown.map(a => (
                                        <tr key={a.area_id} style={{ borderBottom: "1px solid var(--bs-border-color)" }}>
                                            <td className="py-1">{a.area_name}</td>
                                            <td className="py-1 text-end">{a.total.toLocaleString()}</td>
                                            <td className="py-1 text-end" style={{ color: "#10b981" }}>{a.allowed.toLocaleString()}</td>
                                            <td className="py-1 text-end" style={{ color: "#ef4444" }}>{a.denied.toLocaleString()}</td>
                                            <td className="py-1 text-end">
                                                <span style={{ color: a.auth_rate >= 90 ? "#10b981" : a.auth_rate >= 75 ? "#f59e0b" : "#ef4444", fontWeight: 600 }}>
                                                    {a.auth_rate}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ── Right: one tile, allowed + divider + denied ── */}
                <div className="col-12 col-md-7">
                    <div className="border ez-rounded-tile p-3 ez-shadow bg-white">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span style={{ fontSize: "1rem", fontWeight: 600 }}>Top 5</span>
                            {periodToggle}
                        </div>

                        {/* Allowed */}
                        <div className="mb-2 fw-semibold" style={{ fontSize: "0.85rem", color: "#10b981" }}>
                            <i className="bi bi-check-circle me-1" />{t("insights_top_allowed")}
                        </div>
                        {topAllowed.length === 0
                            ? <small className="text-muted">{t("insights_no_data")}</small>
                            : topAllowed.map((entry, i) => {
                                const detail = enrich(entry.plate_no);
                                return (
                                    <div key={i} className="d-flex align-items-center gap-2 py-1 border-bottom flex-wrap">
                                        <span className="fw-bold" style={{ fontSize: "0.88rem" }}>{entry.plate_no}</span>
                                        {detail?.note && <small className="text-muted">{detail.note}</small>}
                                        {detail?.categories?.map(c => (
                                            <span key={c.id} className="badge rounded-pill" style={{ backgroundColor: c.color, color: contrastColor(c.color), fontSize: "0.68rem" }}>{c.name}</span>
                                        ))}
                                        <span className="text-muted ms-auto" style={{ fontSize: "0.78rem" }}>{entry.count}×</span>
                                    </div>
                                );
                            })
                        }

                        <hr style={{ borderColor: 'var(--bs-border-color)', margin: '12px 0' }} />

                        {/* Denied */}
                        <div className="mb-2 fw-semibold" style={{ fontSize: "0.85rem", color: "#ef4444" }}>
                            <i className="bi bi-x-circle me-1" />{t("insights_top_denied")}
                        </div>
                        {topDenied.length === 0
                            ? <small className="text-muted">{t("insights_no_data")}</small>
                            : topDenied.map((entry, i) => (
                                <div key={i} className="d-flex align-items-center gap-2 py-1 border-bottom">
                                    <span className="fw-bold" style={{ fontSize: "0.88rem", flex: 1 }}>{entry.plate_no}</span>
                                    <span className="text-muted" style={{ fontSize: "0.78rem" }}>{entry.count}×</span>
                                    <button
                                        className="btn btn-sm ez-button-light"
                                        style={{ padding: "1px 7px", fontSize: "0.78rem" }}
                                        title={t("insights_quick_add_title")}
                                        onClick={() => setAddPlate(entry.plate_no)}
                                    >
                                        <i className="bi bi-plus-lg" />
                                    </button>
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>

            {addPlate && (
                <QuickAddModal
                    plateNo={addPlate}
                    areas={loadedAreas}
                    selectedAreaLocal={selectedAreaLocal}
                    setPlatesByArea={setPlatesByArea}
                    onClose={() => setAddPlate(null)}
                    onAdded={() => { setAddPlate(null); setRefreshKey(k => k + 1); }}
                />
            )}
        </>
    );
}
